// ENG-2825 (plan D3 / round 3 C3): the one place that reads an inbox row's capability flags.
//
// Every inbox row now states what the toolbar may do with it (`can_download`, `can_label`,
// `can_notify`, `can_assign`, `can_sftp`) and what kind of document it is
// (`reply_document_state`). Before this, eight surfaces each inferred "not a document" from
// the presence of a reply-history `uri` (`isAReplySelected`, `isReplyRow`, `record-row-reply`,
// ...), which is exactly the inference a complete return document breaks: its row carries the
// history link AND is an ordinary document. Nothing outside this file may read `uri` to decide
// what a row can do.
//
// Rows without the flags fall back to the old `uri` inference. That covers a serializer that
// does not emit them and the rolling-deploy window where a new bundle talks to an old API pod;
// in both cases a reply row keeps behaving exactly as it did before the flags existed.

export const CAPABILITIES = Object.freeze({
  DOWNLOAD: 'can_download',
  LABEL: 'can_label',
  NOTIFY: 'can_notify',
  ASSIGN: 'can_assign',
  SFTP: 'can_sftp',
});

export const DOCUMENT_STATES = Object.freeze({
  READY: 'ready',
  PREPARING: 'preparing',
  NEEDS_ATTENTION: 'needs_attention',
  UNAVAILABLE: 'unavailable',
});

const isRow = (record) => Boolean(record) && typeof record === 'object';

// A row that carries a reply message/history (the drawer). True for every reply row, complete
// document or not; says nothing about what the row can do.
export const hasMessageHistory = (record) => isRow(record) && Boolean(record.uri);

// A reply row whose complete return document is ready and openable: it behaves as a document
// everywhere (title opens the viewer, click selects, viewer navigation includes it).
export const isCompleteDocumentRow = (record) => isRow(record)
  && record.reply_document_state === DOCUMENT_STATES.READY
  && Boolean(record.file_token);

// A row whose click opens the message drawer instead of following document conventions: a
// legacy reply, or a new return whose document is not ready yet. Replaces `isReplyRow` and the
// bare `if (uri)` row-click test.
export const opensMessageDrawerOnClick = (record) => hasMessageHistory(record)
  && !isCompleteDocumentRow(record);

// Whether ONE row denies a capability. `false` is the only denial; `undefined` (no flags) falls
// back to the legacy inference described above.
export const rowLacksCapability = (record, capability) => {
  if (!isRow(record)) return false;
  const value = record[capability];
  if (value === undefined) return Boolean(record.uri);
  return value === false;
};

// Whether ANY selected row denies a capability -- the drop-in replacement for the eight
// `isAReplySelected(selectedData)` copies, one call per action so each action asks about
// the capability it actually needs. Ignores the 'all' sentinel the tables put in selections.
export const selectionLacksCapability = (selectedData, capability) => (Array.isArray(selectedData) ? selectedData : [])
  .some((record) => record !== 'all' && rowLacksCapability(record, capability));

// Whether ANY selected row is reply-backed at all -- complete document or not. This is the
// exact predicate the eight `isAReplySelected` copies used (`Boolean(record.uri)`), and it is
// what the toolbar actions that have no capability flag of their own must keep reading: plan
// D3 replaces `isAReplySelected` with the flags ONE-FOR-ONE, and action parity for a ready
// return is ENG-3438 (Phase 2), not this stack. Navigation -- row click, double click, title
// link, viewer previous/next -- is what changes in Phase 1, and that reads
// `opensMessageDrawerOnClick` instead.
export const selectionHasReplyRow = (selectedData) => (Array.isArray(selectedData) ? selectedData : [])
  .some((record) => record !== 'all' && hasMessageHistory(record));

export const documentStateOf = (record) => (isRow(record) ? record.reply_document_state || null : null);
